import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { useTranslation } from 'react-i18next';
import MapsKeysCard from '../components/admin/MapsKeysCard';

const card = {
    background: '#fff', borderRadius: 14, padding: 24, marginBottom: 20,
    border: '1px solid var(--sp-gray-100)'
};
const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--sp-gray-200)',
    boxSizing: 'border-box', fontSize: 14
};

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
}

export default function AdminIntegrationsPage() {
    const { t } = useTranslation(['common', 'nav', 'messages', 'errors', 'admin']);
    const role = localStorage.getItem('role') || '';
    const [data, setData] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [testingTelegram, setTestingTelegram] = useState(false);
    const [testingAdminNotify, setTestingAdminNotify] = useState(false);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        if (role !== 'Admin') return;
        load();
    }, [role]);

    async function load() {
        try {
            const res = await adminApi().get('/admin/integrations');
            setData(res.data);
            setForm({
                yooKassaShopId: res.data.yooKassaShopId || '',
                yooKassaSecretKey: '',
                smsRuApiId: '',
                smsRuSenderName: res.data.smsRuSenderName || 'Sportprogress',
                smsRuTestMode: res.data.smsRuTestMode,
                smsProviderCostPerMessage: res.data.smsProviderCostPerMessage,
                smsSchoolPricePerMessage: res.data.smsSchoolPricePerMessage,
                smsMonthlySubscriptionFee: res.data.smsMonthlySubscriptionFee,
                smsMinimumDeposit: res.data.smsMinimumDeposit,
                telegramBotToken: '',
                bugReportChatId: res.data.bugReportChatId || '',
                adminAlertChatId: res.data.adminAlertChatId || '',
                reportDailyEnabled: !!res.data.reportDailyEnabled,
                reportWeeklyEnabled: !!res.data.reportWeeklyEnabled,
                reportMonthlyEnabled: res.data.reportMonthlyEnabled !== false,
                reportAnnualEnabled: !!res.data.reportAnnualEnabled,
                telegramProxyEnabled: !!res.data.telegramProxyEnabled,
                telegramProxyType: res.data.telegramProxyType || 'http',
                telegramProxyHost: res.data.telegramProxyHost || '',
                telegramProxyPort: res.data.telegramProxyPort || '',
                telegramProxyUsername: res.data.telegramProxyUsername || '',
                telegramProxyPassword: '',
            });
        } catch {
            setMsg(t('admin:integrations.loadError'));
        }
    }

    async function save(e) {
        e.preventDefault();
        setSaving(true);
        setMsg('');
        try {
            const body = {
                yooKassaShopId: form.yooKassaShopId,
                smsRuSenderName: form.smsRuSenderName,
                smsRuTestMode: form.smsRuTestMode,
                smsProviderCostPerMessage: Number(form.smsProviderCostPerMessage),
                smsSchoolPricePerMessage: Number(form.smsSchoolPricePerMessage),
                smsMonthlySubscriptionFee: Number(form.smsMonthlySubscriptionFee),
                smsMinimumDeposit: Number(form.smsMinimumDeposit)
            };
            if (form.yooKassaSecretKey) body.yooKassaSecretKey = form.yooKassaSecretKey;
            if (form.smsRuApiId) body.smsRuApiId = form.smsRuApiId;
            if (form.telegramBotToken) body.telegramBotToken = form.telegramBotToken;
            body.bugReportChatId = form.bugReportChatId ?? '';
            body.adminAlertChatId = form.adminAlertChatId ?? '';
            body.reportDailyEnabled = !!form.reportDailyEnabled;
            body.reportWeeklyEnabled = !!form.reportWeeklyEnabled;
            body.reportMonthlyEnabled = !!form.reportMonthlyEnabled;
            body.reportAnnualEnabled = !!form.reportAnnualEnabled;
            body.telegramProxyEnabled = !!form.telegramProxyEnabled;
            body.telegramProxyType = form.telegramProxyType || 'http';
            body.telegramProxyHost = form.telegramProxyHost ?? '';
            body.telegramProxyPort = form.telegramProxyPort === '' ? 0 : Number(form.telegramProxyPort);
            body.telegramProxyUsername = form.telegramProxyUsername ?? '';
            if (form.telegramProxyPassword) body.telegramProxyPassword = form.telegramProxyPassword;
            await adminApi().put('/admin/integrations', body);
            setMsg(t('admin:integrations.saved'));
            load();
        } catch (err) {
            setMsg(err.response?.data?.message || t('admin:integrations.saveError'));
        }
        setSaving(false);
    }

    async function testTelegram() {
        setTestingTelegram(true);
        setMsg('');
        try {
            const body = {};
            if (form.telegramBotToken) body.telegramBotToken = form.telegramBotToken;
            if (form.bugReportChatId) body.bugReportChatId = form.bugReportChatId;
            body.telegramProxyEnabled = !!form.telegramProxyEnabled;
            body.telegramProxyType = form.telegramProxyType || 'http';
            body.telegramProxyHost = form.telegramProxyHost ?? '';
            body.telegramProxyPort = form.telegramProxyPort === '' ? 0 : Number(form.telegramProxyPort);
            body.telegramProxyUsername = form.telegramProxyUsername ?? '';
            if (form.telegramProxyPassword) body.telegramProxyPassword = form.telegramProxyPassword;
            const res = await adminApi().post('/admin/integrations/test-telegram', body);
            if (res.data?.success) {
                setMsg(t('admin:integrations.telegramTestOk'));
            } else {
                setMsg(t('admin:integrations.telegramTestFail', { error: res.data?.error || '?' }));
            }
        } catch (err) {
            setMsg(t('admin:integrations.telegramTestFail', { error: err.message }));
        }
        setTestingTelegram(false);
    }

    async function testAdminNotify() {
        setTestingAdminNotify(true);
        setMsg('');
        try {
            const res = await adminApi().post('/admin/notifications/test-admin-notify');
            const { emailSent, telegramSent } = res.data ?? {};
            if (emailSent || telegramSent) {
                setMsg(t('admin:integrations.adminNotifyTestOk', { emailSent, telegramSent }));
            } else {
                setMsg(t('admin:integrations.adminNotifyTestFail'));
            }
        } catch (err) {
            setMsg(err.response?.data?.message || t('admin:integrations.adminNotifyTestFail'));
        }
        setTestingAdminNotify(false);
    }

    if (role !== 'Admin') {
        return (
            <OwnerPageLayout title={t('admin:integrations.title')} maxWidth={720}>
                <p>{t('admin:integrations.adminOnly')}</p>
            </OwnerPageLayout>
        );
    }

    const savedMsg = t('admin:integrations.saved');

    return (
        <OwnerPageLayout
            title={t('admin:integrations.title')}
            subtitle={t('admin:integrations.subtitle')}
            maxWidth={720}
        >
                <MapsKeysCard />
                <p style={{ marginBottom: 8 }}>
                    <a href="/admin/email-settings" style={{ color: '#EB3333', fontWeight: 600 }}>
                        {t('admin:integrations.emailSettingsLink')}
                    </a>
                </p>
                <p style={{ marginBottom: 8 }}>
                    <a href="/admin/sms-billing" style={{ color: '#EB3333', fontWeight: 600 }}>
                        {t('admin:integrations.smsBillingLink')}
                    </a>
                </p>
                <p style={{ marginBottom: 16 }}>
                    <a href="/admin/bugs" style={{ color: '#EB3333', fontWeight: 600 }}>
                        {t('admin:integrations.bugsLink')}
                    </a>
                </p>

                {data && (
                    <form onSubmit={save}>
                        <div style={card}>
                            <h2 style={{ marginTop: 0 }}>{t('admin:integrations.yooKassaTitle')}</h2>
                            <label style={{ fontSize: 13, fontWeight: 600 }}>{t('admin:integrations.shopId')}</label>
                            <input style={inputStyle} value={form.yooKassaShopId || ''}
                                onChange={e => setForm({ ...form, yooKassaShopId: e.target.value })} />
                            <label style={{ fontSize: 13, fontWeight: 600, marginTop: 12, display: 'block' }}>
                                {t('admin:integrations.secretKey')}{' '}
                                {data.hasYooKassaSecretKey && t('admin:integrations.secretAlreadySet')}
                            </label>
                            <input type="password" style={inputStyle} value={form.yooKassaSecretKey || ''}
                                onChange={e => setForm({ ...form, yooKassaSecretKey: e.target.value })}
                                placeholder={t('admin:integrations.secretPlaceholder')} autoComplete="off" />
                        </div>

                        <div style={card}>
                            <h2 style={{ marginTop: 0 }}>{t('admin:integrations.smsRuTitle')}</h2>
                            <label style={{ fontSize: 13, fontWeight: 600 }}>
                                {t('admin:integrations.apiId')}{' '}
                                {data.hasSmsRuApiId && t('admin:integrations.apiIdAlreadySet')}
                            </label>
                            <input type="password" style={inputStyle} value={form.smsRuApiId || ''}
                                onChange={e => setForm({ ...form, smsRuApiId: e.target.value })} />
                            <label style={{ fontSize: 13, fontWeight: 600, marginTop: 12, display: 'block' }}>
                                {t('admin:integrations.senderName')}
                            </label>
                            <input style={inputStyle} value={form.smsRuSenderName || ''}
                                onChange={e => setForm({ ...form, smsRuSenderName: e.target.value })} />
                            <label style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type="checkbox" checked={!!form.smsRuTestMode}
                                    onChange={e => setForm({ ...form, smsRuTestMode: e.target.checked })} />
                                {t('admin:integrations.testMode')}
                            </label>
                        </div>

                        <div style={card}>
                            <h2 style={{ marginTop: 0 }}>🐛 {t('admin:integrations.telegramTitle')}</h2>
                            <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
                                {t('admin:integrations.telegramHint')}
                            </p>
                            <p style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: data.telegramConfigured ? '#2e7d32' : '#c62828',
                                marginBottom: 12,
                            }}>
                                {data.telegramConfigured
                                    ? t('admin:integrations.telegramConfigured')
                                    : t('admin:integrations.telegramNotConfigured')}
                            </p>
                            <label style={{ fontSize: 13, fontWeight: 600 }}>
                                {t('admin:integrations.telegramBotToken')}{' '}
                                {data.hasTelegramBotToken && t('admin:integrations.telegramBotTokenSet')}
                            </label>
                            <input
                                type="password"
                                style={inputStyle}
                                value={form.telegramBotToken || ''}
                                onChange={e => setForm({ ...form, telegramBotToken: e.target.value })}
                                placeholder={t('admin:integrations.telegramBotTokenPlaceholder')}
                                autoComplete="off"
                            />
                            <label style={{ fontSize: 13, fontWeight: 600, marginTop: 12, display: 'block' }}>
                                {t('admin:integrations.telegramChatId')}
                            </label>
                            <input
                                style={inputStyle}
                                value={form.bugReportChatId || ''}
                                onChange={e => setForm({ ...form, bugReportChatId: e.target.value })}
                                placeholder={t('admin:integrations.telegramChatIdPlaceholder')}
                            />
                            <button
                                type="button"
                                disabled={testingTelegram}
                                onClick={testTelegram}
                                style={{
                                    marginTop: 12,
                                    padding: '10px 18px',
                                    background: '#0088cc',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 10,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                {testingTelegram ? t('admin:integrations.saving') : t('admin:integrations.telegramTest')}
                            </button>

                            <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid var(--sp-gray-100)' }} />

                            <h3 style={{ marginTop: 0, fontSize: 15 }}>{t('admin:integrations.telegramProxyTitle')}</h3>
                            <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
                                {t('admin:integrations.telegramProxyHint')}
                            </p>
                            <label style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type="checkbox" checked={!!form.telegramProxyEnabled}
                                    onChange={e => setForm({ ...form, telegramProxyEnabled: e.target.checked })} />
                                {t('admin:integrations.telegramProxyEnabled')}
                            </label>
                            <label style={{ fontSize: 13, fontWeight: 600, marginTop: 12, display: 'block' }}>
                                {t('admin:integrations.telegramProxyType')}
                            </label>
                            <select
                                style={inputStyle}
                                value={form.telegramProxyType || 'http'}
                                onChange={e => setForm({ ...form, telegramProxyType: e.target.value })}
                            >
                                <option value="http">{t('admin:integrations.telegramProxyTypeHttp')}</option>
                                <option value="https">{t('admin:integrations.telegramProxyTypeHttps')}</option>
                                <option value="socks5">{t('admin:integrations.telegramProxyTypeSocks5')}</option>
                            </select>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginTop: 12 }}>
                                <div>
                                    <label style={{ fontSize: 13, fontWeight: 600 }}>{t('admin:integrations.telegramProxyHost')}</label>
                                    <input
                                        style={inputStyle}
                                        value={form.telegramProxyHost || ''}
                                        onChange={e => setForm({ ...form, telegramProxyHost: e.target.value })}
                                        placeholder={t('admin:integrations.telegramProxyHostPlaceholder')}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: 13, fontWeight: 600 }}>{t('admin:integrations.telegramProxyPort')}</label>
                                    <input
                                        type="number"
                                        style={inputStyle}
                                        value={form.telegramProxyPort ?? ''}
                                        onChange={e => setForm({ ...form, telegramProxyPort: e.target.value })}
                                        placeholder={t('admin:integrations.telegramProxyPortPlaceholder')}
                                    />
                                </div>
                            </div>
                            <label style={{ fontSize: 13, fontWeight: 600, marginTop: 12, display: 'block' }}>
                                {t('admin:integrations.telegramProxyUsername')}
                            </label>
                            <input
                                style={inputStyle}
                                value={form.telegramProxyUsername || ''}
                                onChange={e => setForm({ ...form, telegramProxyUsername: e.target.value })}
                                autoComplete="off"
                            />
                            <label style={{ fontSize: 13, fontWeight: 600, marginTop: 12, display: 'block' }}>
                                {t('admin:integrations.telegramProxyPassword')}{' '}
                                {data.hasTelegramProxyPassword && t('admin:integrations.telegramProxyPasswordSet')}
                            </label>
                            <input
                                type="password"
                                style={inputStyle}
                                value={form.telegramProxyPassword || ''}
                                onChange={e => setForm({ ...form, telegramProxyPassword: e.target.value })}
                                autoComplete="new-password"
                            />
                        </div>

                        <div style={card}>
                            <h2 style={{ marginTop: 0 }}>🔔 Уведомления администратора</h2>
                            <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
                                Периодические отчёты на email и в Telegram. Email берётся из AdminNotificationEmail (настройки SMTP).
                            </p>
                            <label style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type="checkbox" checked={!!form.reportDailyEnabled}
                                    onChange={e => setForm({ ...form, reportDailyEnabled: e.target.checked })} />
                                Ежедневный отчёт
                            </label>
                            <label style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type="checkbox" checked={!!form.reportWeeklyEnabled}
                                    onChange={e => setForm({ ...form, reportWeeklyEnabled: e.target.checked })} />
                                Еженедельный отчёт (по понедельникам)
                            </label>
                            <label style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type="checkbox" checked={form.reportMonthlyEnabled !== false}
                                    onChange={e => setForm({ ...form, reportMonthlyEnabled: e.target.checked })} />
                                Ежемесячный отчёт (1-го числа)
                            </label>
                            <label style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type="checkbox" checked={!!form.reportAnnualEnabled}
                                    onChange={e => setForm({ ...form, reportAnnualEnabled: e.target.checked })} />
                                Ежегодный отчёт (1 января)
                            </label>
                            <label style={{ fontSize: 13, fontWeight: 600, marginTop: 16, display: 'block' }}>
                                Telegram Chat ID для уведомлений
                            </label>
                            <input
                                style={inputStyle}
                                value={form.adminAlertChatId || ''}
                                onChange={e => setForm({ ...form, adminAlertChatId: e.target.value })}
                                placeholder="Отдельно от BugReportChatId"
                            />
                            <button
                                type="button"
                                onClick={testAdminNotify}
                                disabled={testingAdminNotify}
                                style={{
                                    marginTop: 12,
                                    padding: '10px 16px',
                                    background: '#EB3333',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 10,
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                            >
                                {testingAdminNotify
                                    ? t('admin:integrations.adminNotifyTesting')
                                    : t('admin:integrations.adminNotifyTest')}
                            </button>
                        </div>

                        <div style={card}>
                            <h2 style={{ marginTop: 0 }}>{t('admin:integrations.smsTariffsTitle')}</h2>
                            <p style={{ fontSize: 13, color: '#64748b' }}>
                                {t('admin:integrations.smsTariffsHint')}
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600 }}>{t('admin:integrations.providerCost')}</label>
                                    <input type="number" style={inputStyle} value={form.smsProviderCostPerMessage}
                                        onChange={e => setForm({ ...form, smsProviderCostPerMessage: e.target.value })} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600 }}>{t('admin:integrations.schoolPrice')}</label>
                                    <input type="number" style={inputStyle} value={form.smsSchoolPricePerMessage}
                                        onChange={e => setForm({ ...form, smsSchoolPricePerMessage: e.target.value })} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600 }}>{t('admin:integrations.monthlyFee')}</label>
                                    <input type="number" style={inputStyle} value={form.smsMonthlySubscriptionFee}
                                        onChange={e => setForm({ ...form, smsMonthlySubscriptionFee: e.target.value })} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600 }}>{t('admin:integrations.minDeposit')}</label>
                                    <input type="number" style={inputStyle} value={form.smsMinimumDeposit}
                                        onChange={e => setForm({ ...form, smsMinimumDeposit: e.target.value })} />
                                </div>
                            </div>
                        </div>

                        <button type="submit" disabled={saving} style={{
                            padding: '14px 28px', background: '#1A1A1A', color: '#fff',
                            border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer'
                        }}>
                            {saving ? t('admin:integrations.saving') : t('common:save')}
                        </button>
                        {msg && (
                            <p style={{ marginTop: 12, color: msg === savedMsg ? '#2e7d32' : '#c62828' }}>{msg}</p>
                        )}
                    </form>
                )}
        </OwnerPageLayout>
    );
}
